import { useCallback, useEffect, useMemo, useState } from 'react';

import { ApiError, api } from '../api/client';
import { ChartRenderer } from '../components/ChartRenderer';
import {
  FilterBar,
  STORE_FILTER,
  type FilterSpec,
  type TimeframeChoice,
} from '../components/FilterBar';
import { canTime, tileParams } from '../dashboardParams';
import { DEFAULT_TIMEFRAME_DAYS, rangeForDays, validRange } from '../timeframe';
import { ChartError, ChartLoading, Empty } from '../charts/states';
import { SignedOut } from '../components/SignedOut';
import { appCount, embedParams, hasAppSelection } from '../embedParams';
import type { CatalogChart, FilterOption, SavedChart, SavedDashboard } from '../types';

/**
 * What the platform embeds: `/d/<slug>?app_id=…` (or `app_ids=…`).
 *
 * There is no header inside this view on purpose. The platform already draws
 * the page title, the app picker and the light/dark toggle around the iframe;
 * repeating any of them here would give the reader two of everything and two
 * places to change the same setting.
 */

interface Props {
  slug: string;
}

export const DashboardView = ({ slug }: Props) => {
  const [dashboard, setDashboard] = useState<SavedDashboard | null>(null);
  const [charts, setCharts] = useState<SavedChart[]>([]);
  /** Per source — chart ids are only unique within one. */
  const [catalog, setCatalog] = useState<Record<string, CatalogChart[]>>({});
  /** The catalogue says which filters each tile accepts; configured dashboards wait for it. */
  const [catalogReady, setCatalogReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [signedOut, setSignedOut] = useState(false);
  /** What the viewer has chosen. Merged over the embed's own params. */
  const [selection, setSelection] = useState<Record<string, string>>({});
  /**
   * Category values for the chosen store.
   *
   * Per store, not global: the two taxonomies barely overlap — Android has
   * "Art and design", iTunes has "Photo & Video" — and passing one store's
   * value to the other returns zero rows rather than an error, so a merged
   * list would silently show empty charts.
   */
  const [categories, setCategories] = useState<FilterOption[]>([]);
  const [categoriesFor, setCategoriesFor] = useState<string>('');
  /**
   * The viewer's time frame. A `from`/`to` already on the embed URL wins as the
   * starting point (the platform chose it); otherwise the dashboard's own
   * default window, last 30 days unless the author set another.
   */
  const [timeframe, setTimeframe] = useState<TimeframeChoice>(() => {
    const url = embedParams();
    return validRange(url.from ?? '', url.to ?? '')
      ? { mode: 'custom', from: url.from, to: url.to }
      : { mode: 'preset', days: DEFAULT_TIMEFRAME_DAYS };
  });

  useEffect(() => {
    Promise.all([api.dashboard(slug), api.charts()])
      .then(([d, c]) => {
        setDashboard(d);
        const days = d.filters?.timeframe.defaultDays;
        if (days) {
          setTimeframe((current) => (current.mode === 'preset' ? { mode: 'preset', days } : current));
        }
        setCharts(c);
        // The catalogue is what says which parameters each query accepts, so it
        // decides which filters this dashboard can honestly offer.
        const sources = [...new Set(d.tiles
          .map((t) => c.find((x) => x.id === t.chartId)?.source)
          .filter(Boolean) as string[])];
        return Promise.all(sources.map((s) => api.catalog(s).then((list) => [s, list] as const)));
      })
      .then((lists) => {
        setCatalog(Object.fromEntries(lists ?? []));
        setCatalogReady(true);
      })
      .catch((err: Error) => {
        if (err instanceof ApiError && err.status === 401) setSignedOut(true);
        else setError(err.message);
      });
  }, [slug]);

  const store = selection.platform ?? '';

  useEffect(() => {
    // Drop a category chosen for the previous store. Android's "Comics" is not
    // an iTunes value, and the API answers a wrong one with zero rows rather
    // than an error — so leaving it set turns a store change into an empty
    // dashboard that looks like missing data.
    setSelection((current) => {
      if (!current.category) return current;
      const next = { ...current };
      delete next.category;
      return next;
    });

    if (!store) {
      setCategories([]);
      setCategoriesFor('');
      return;
    }
    let cancelled = false;
    api
      .categories('appstore', store)
      .then((list) => {
        if (cancelled) return;
        setCategories(list);
        setCategoriesFor(store);
      })
      .catch(() => {
        // A missing list disables the control rather than failing the report —
        // every tile still renders, just without this filter.
        if (cancelled) return;
        setCategories([]);
        setCategoriesFor('');
      });
    return () => {
      cancelled = true;
    };
  }, [store]);

  /** What a tile's query accepts, or null when the catalogue can't say (composite charts). */
  const acceptedBy = useCallback(
    (chart: SavedChart | undefined): string[] | null =>
      (chart && catalog[chart.source]?.find((c) => c.chartId === chart.chartId)?.params) ?? null,
    [catalog],
  );

  /**
   * Only offer a filter every tile can honour. A control that silently does
   * nothing to half the dashboard is worse than no control.
   */
  const filters = useMemo<FilterSpec[]>(() => {
    if (!dashboard || !Object.keys(catalog).length) return [];

    const tileParams = dashboard.tiles.map((tile) => {
      const chart = charts.find((c) => c.id === tile.chartId);
      return acceptedBy(chart) ?? [];
    });
    if (!tileParams.length) return [];
    // A dashboard with explicit filter settings offers what the author switched
    // on, applied per tile (see paramsFor) — so one tile that cannot honour it
    // does not hide the control. A dashboard saved before settings existed keeps
    // the stricter rule: only a filter every tile accepts.
    const configured = dashboard.filters;
    const offered = (param: string, enabled: boolean) =>
      configured
        ? enabled && tileParams.some((p) => p.includes(param))
        : tileParams.every((p) => p.includes(param));

    const specs: FilterSpec[] = [];
    if (offered('platform', configured?.platform ?? true)) specs.push(STORE_FILTER);
    if (offered('category', configured?.category ?? true)) {
      specs.push({
        param: 'category',
        label: 'Category',
        // Only once the list for THIS store has arrived. Showing the previous
        // store's categories for even a moment would offer values that return
        // nothing, which reads as missing data rather than a wrong choice.
        options: categoriesFor === store ? categories : [],
        unavailable: store
          ? 'Loading categories…'
          : 'Choose a store first — categories differ between them',
      });
    }
    return specs;
  }, [dashboard, charts, catalog, acceptedBy, categories, categoriesFor, store]);

  if (signedOut) return <SignedOut context="embed" />;
  if (error) return <ChartError title="Report unavailable" message={error} />;
  if (!dashboard) return <ChartLoading label="Loading report" size={72} />;
  // Until the catalogue arrives every tile would look like it accepts every
  // filter, and would query once with params it may reject before re-querying.
  if (dashboard.filters && !catalogReady) return <ChartLoading label="Loading report" size={72} />;

  const params = embedParams();

  const timeframeCfg = dashboard.filters?.timeframe;
  const timeTiles = dashboard.tiles
    .map((t) => charts.find((c) => c.id === t.chartId))
    .filter((c): c is SavedChart => Boolean(c));
  const timedCount = timeTiles.filter((c) => canTime(acceptedBy(c))).length;
  const showTimeframe = Boolean(timeframeCfg?.enabled) && timedCount > 0;

  const range = !showTimeframe
    ? {}
    : timeframe.mode === 'preset'
      ? rangeForDays(timeframe.days)
      : validRange(timeframe.from, timeframe.to)
        ? { from: timeframe.from, to: timeframe.to }
        : {};

  const paramsFor = (chart: SavedChart): Record<string, string> =>
    tileParams({
      configured: Boolean(dashboard.filters),
      embed: params,
      selection,
      accepted: acceptedBy(chart),
      range,
    });

  if (dashboard.appFilter !== 'none' && !hasAppSelection(params)) {
    return (
      <Empty
        message={`Select ${dashboard.appFilter === 'single' ? 'an app' : 'one or more apps'} to view this report.`}
      />
    );
  }

  const selected = appCount(params);

  return (
    <div className="dashboard">
      <FilterBar
        filters={filters}
        value={selection}
        onChange={setSelection}
        timeframe={
          showTimeframe
            ? {
                value: timeframe,
                onChange: setTimeframe,
                note:
                  timedCount < timeTiles.length
                    ? `Applies to ${timedCount} of ${timeTiles.length} charts`
                    : undefined,
              }
            : undefined
        }
      />

      <div className="dashboard__grid">
        {dashboard.tiles.map((tile, i) => {
          const chart = charts.find((c) => c.id === tile.chartId);
          if (!chart) return null;

          return (
            <div
              className="dashboard__tile"
              key={`${tile.chartId}-${i}`}
              style={{
                gridColumn: `span ${Math.min(12, Math.max(1, tile.width))}`,
                minHeight: tile.height * 56,
              }}
            >
              <ChartRenderer
                chart={chart}
                overrideParams={paramsFor(chart)}
                title={tile.title ?? chart.name}
                description={tile.description}
              />
            </div>
          );
        })}
      </div>

      {selected > 1 && (
        <p className="dashboard__scope">Aggregated across {selected} selected apps.</p>
      )}
    </div>
  );
};

/**
 * Single-chart embed for widget tiles: `/w/<widgetId>`.
 *
 * The platform wraps each widget in its own card with its own heading, and the
 * card's aspect ratio is fixed — so this renders chrome-less and must not
 * scroll. Anything that would overflow belongs in a dashboard, not a widget.
 */
export const WidgetView = ({ widgetId }: { widgetId: string }) => {
  const [chart, setChart] = useState<SavedChart | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [signedOut, setSignedOut] = useState(false);

  useEffect(() => {
    Promise.all([api.widgets(), api.charts()])
      .then(([widgets, charts]) => {
        const widget = widgets.find((w) => w.id === widgetId);
        if (!widget) throw new Error('Widget not found');

        const found = charts.find((c) => c.id === widget.chartId);
        if (!found) throw new Error('The widget points at a chart that no longer exists');

        setChart(found);
      })
      .catch((err: Error) => {
        if (err instanceof ApiError && err.status === 401) setSignedOut(true);
        else setError(err.message);
      });
  }, [widgetId]);

  if (signedOut) return <SignedOut context="embed" />;
  if (error) return <ChartError title="Widget unavailable" message={error} />;
  if (!chart) return <ChartLoading />;

  return (
    <div className="widget-view">
      <ChartRenderer chart={chart} overrideParams={embedParams()} chrome={false} />
    </div>
  );
};
